import { useState, useRef, useEffect, useCallback, useMemo, CSSProperties } from "react";
import { useFormContext } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { getDiagnosesForSpecialty } from "@/lib/constants";

export default function DiagnosisSection({ specialty = "General Medicine" }: { specialty?: string }) {
    const { setValue, watch, formState: { errors } } = useFormContext();
    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const getDropdownStyle = (): CSSProperties => {
        if (!inputRef.current) return {};
        const rect = inputRef.current.getBoundingClientRect();
        const dropdownHeight = Math.min(8 * 36 + 8, 224);
        const wouldOverflowBottom = rect.bottom + dropdownHeight > window.innerHeight - 20;
        const wouldOverflowRight = rect.left + 280 > window.innerWidth - 20;
        return {
            position: "fixed",
            minWidth: 280,
            zIndex:   9999,
            ...(wouldOverflowRight ? { right: window.innerWidth - rect.right } : { left: rect.left }),
            ...(wouldOverflowBottom
                ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                : { top: rect.bottom + 2 }),
        };
    };

    const formValue: string = watch("diagnosis") || "";

    const selectedTags = useMemo(() => {
        if (typeof formValue !== "string") return [];
        return formValue.split(",").map((s) => s.trim()).filter(Boolean);
    }, [formValue]);

    const MAX_DIAGNOSIS_LENGTH = 2000;

    const updateFormValue = useCallback((tagsArray: string[]) => {
        const joined = tagsArray.join(", ");
        if (joined.length <= MAX_DIAGNOSIS_LENGTH)
            setValue("diagnosis", joined, { shouldDirty: true });
    }, [setValue]);

    const allDiagnoses: string[] = useMemo(() => {
        const diagnoses = getDiagnosesForSpecialty(specialty);
        const seen = new Set<string>();
        const result: string[] = [];
        if (Array.isArray(diagnoses)) {
            for (const d of diagnoses) {
                if (!seen.has(d)) { seen.add(d); result.push(d); }
            }
        }
        return result;
    }, [specialty]);

    const filteredSuggestions = useMemo(() => {
        const searchLower = inputValue.toLowerCase().trim();
        const unselected = allDiagnoses.filter((d) => !selectedTags.includes(d));
        if (!searchLower) return unselected.slice(0, 8);

        const exact: string[] = [], startsWith: string[] = [], wordStart: string[] = [], contains: string[] = [];
        for (const d of unselected) {
            const dl = d.toLowerCase();
            if (dl === searchLower) exact.push(d);
            else if (dl.startsWith(searchLower)) startsWith.push(d);
            else if (d.split(/[\s/]/).some((w) => w.toLowerCase().startsWith(searchLower))) wordStart.push(d);
            else if (dl.includes(searchLower)) contains.push(d);
        }
        return [...exact, ...startsWith, ...wordStart, ...contains].slice(0, 8);
    }, [inputValue, allDiagnoses, selectedTags]);

    useEffect(() => {
        setFocusedIndex(showSuggestions && filteredSuggestions.length > 0 ? 0 : -1);
    }, [showSuggestions, filteredSuggestions.length]);

    const addTag = useCallback((diagnosis: string) => {
        const trimmed = diagnosis.trim();
        if (trimmed && !selectedTags.includes(trimmed)) {
            updateFormValue([...selectedTags, trimmed]);
        }
        setInputValue("");
        setTimeout(() => {
            inputRef.current?.focus();
            setShowSuggestions(true);
        }, 0);
    }, [selectedTags, updateFormValue]);

    const removeTag = useCallback((name: string) => {
        updateFormValue(selectedTags.filter((t) => t !== name));
    }, [selectedTags, updateFormValue]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const max = filteredSuggestions.length - 1;
        switch (e.key) {
            case "ArrowDown": e.preventDefault(); setFocusedIndex((p) => (p < max ? p + 1 : 0)); break;
            case "ArrowUp": e.preventDefault(); setFocusedIndex((p) => (p > 0 ? p - 1 : max)); break;
            case "Enter":
            case ",":
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
                    addTag(filteredSuggestions[focusedIndex]);
                } else if (inputValue.trim()) {
                    addTag(inputValue.trim());
                }
                break;
            case "Escape": setShowSuggestions(false); break;
            case "Backspace":
                if (inputValue === "" && selectedTags.length > 0) {
                    e.preventDefault();
                    updateFormValue(selectedTags.slice(0, -1));
                }
                break;
            case "Tab": setShowSuggestions(false); break;
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="space-y-2" ref={containerRef}>
            <div
                className={cn(
                    "min-h-[42px] p-2 border border-border rounded-lg cursor-text transition-all bg-card flex items-center",
                    "hover:border-ring/60",
                    showSuggestions && "ring-2 ring-ring/20 border-ring",
                    errors.diagnosis && "border-destructive/30 bg-destructive/5"
                )}
                onClick={() => { inputRef.current?.focus(); setShowSuggestions(true); }}
            >
                <div className="flex flex-wrap items-center gap-1.5 w-full">
                    {selectedTags.map((name, idx) => (
                        <Badge key={`${name}-${idx}`} variant="secondary" className="h-6 px-2 text-xs bg-primary/10 text-primary border border-primary/30">
                            <span className="font-medium">{name}</span>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeTag(name); }} className="ml-1.5 p-1 -m-1 hover:text-destructive">
                                <X className="w-3 h-3" />
                            </button>
                        </Badge>
                    ))}
                    <input
                        ref={inputRef} type="text" value={inputValue}
                        onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
                        onFocus={() => setShowSuggestions(true)} onKeyDown={handleKeyDown}
                        placeholder={selectedTags.length === 0 ? "Type diagnosis... (Enter or comma to add)" : "Add another..."}
                        className="flex-1 min-w-[180px] h-7 bg-transparent border-0 outline-none text-xs text-foreground placeholder:text-muted-foreground/40 placeholder:font-normal"
                    />
                </div>
            </div>

            {showSuggestions && (filteredSuggestions.length > 0 || inputValue.trim()) && (
                <div
                    className="bg-popover border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto"
                    style={getDropdownStyle()}
                >
                    {filteredSuggestions.map((diagnosis, index) => (
                        <button
                            key={diagnosis} type="button"
                            onMouseDown={(e) => { e.preventDefault(); addTag(diagnosis); }}
                            className={cn(
                                "w-full text-left px-3 py-2 text-xs border-b border-border/40 last:border-0 flex items-center justify-between",
                                index === focusedIndex
                                    ? "bg-primary/10 text-primary"
                                    : "hover:bg-accent text-foreground"
                            )}
                        >
                            <span>{diagnosis}</span>
                            <Plus className="w-3 h-3" />
                        </button>
                    ))}
                    {inputValue.trim() && !filteredSuggestions.some(t => t.toLowerCase() === inputValue.toLowerCase().trim()) && (
                        <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); addTag(inputValue.trim()); }}
                            className="w-full text-left px-3 py-2 text-xs border-t border-border/40 flex items-center justify-between text-muted-foreground hover:bg-accent"
                        >
                            <span>Add: "{inputValue.trim()}"</span>
                            <Plus className="w-3 h-3 text-[hsl(var(--success))]" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}